<template>
  <b-modal
    v-model="modalVisible"
    :title="mode === 'edit' ? 'Edit Manual Reading' : 'Add Manual Reading'"
    :ok-title="mode === 'edit' ? 'Save Changes' : 'Add Reading'"
    :ok-disabled="!isValid || saving"
    :cancel-disabled="saving"
    :no-close-on-backdrop="saving"
    :no-close-on-esc="saving"
    @ok="submit"
    @hidden="onHidden"
  >
    <b-form @submit.prevent="submit">
      <b-form-group v-if="device" class="mb-2">
        <small class="text-muted">
          <i class="fa fa-microchip mr-1"></i>
          <strong>{{ device.name }}</strong>
          <span v-if="device.serial"> &mdash; <code>{{ device.serial }}</code></span>
          <span v-if="device.type"> &mdash; {{ device.type }}</span>
        </small>
      </b-form-group>

      <b-row>
        <b-col md="6">
          <b-form-group label="Metric *" label-for="mr-metric">
            <b-form-select
              id="mr-metric"
              v-model="form.metricName"
              :options="metricOptions"
              :disabled="saving"
              required
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Timestamp *" label-for="mr-timestamp">
            <b-form-input
              id="mr-timestamp"
              v-model="form.timestampLocal"
              type="datetime-local"
              :disabled="saving"
              required
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group label="Value *" label-for="mr-value">
            <b-form-input
              id="mr-value"
              v-model.number="form.value"
              type="number"
              step="any"
              :disabled="saving"
              required
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Unit" label-for="mr-unit">
            <b-form-input
              id="mr-unit"
              v-model="form.unit"
              placeholder="e.g. ft, %, °F"
              :disabled="saving"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="Notes" label-for="mr-notes">
        <b-form-textarea
          id="mr-notes"
          v-model="form.notes"
          rows="2"
          placeholder="Optional context (instrument used, observer, weather, etc.)"
          :disabled="saving"
        />
      </b-form-group>

      <b-row>
        <b-col md="6">
          <b-form-checkbox v-model="form.isManual" :disabled="saving" switch>
            Manual reading
          </b-form-checkbox>
          <small class="text-muted d-block">Recorded by a person (not a sensor).</small>
        </b-col>
        <b-col md="6">
          <b-form-checkbox v-model="form.isFieldVerified" :disabled="saving" switch>
            Field verified
          </b-form-checkbox>
          <small class="text-muted d-block">Taken in the field with confirmed instrument.</small>
        </b-col>
      </b-row>
    </b-form>
  </b-modal>
</template>

<script>
import moment from 'moment-timezone'
import { DEVICE_METRICS } from '@/constants/data'
import {
  CREATE_MANUAL_READING_MUTATION,
  UPDATE_MANUAL_READING_MUTATION
} from '@/constants/graphql'

const DEFAULT_FORM = () => ({
  metricName: '',
  value: null,
  unit: '',
  timestampLocal: moment().format('YYYY-MM-DDTHH:mm'),
  notes: '',
  isManual: true,
  isFieldVerified: false
})

export default {
  name: 'ManualReadingModal',
  props: {
    device: {
      type: Object,
      default: null
    },
    visible: {
      type: Boolean,
      default: false
    },
    defaultMetric: {
      type: String,
      default: null
    },
    // pass an existing reading to switch into edit mode
    reading: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      saving: false,
      form: DEFAULT_FORM()
    }
  },
  computed: {
    modalVisible: {
      get () { return this.visible },
      set (v) { if (!v) this.$emit('close') }
    },
    mode () {
      return this.reading && this.reading.id ? 'edit' : 'create'
    },
    metricOptions () {
      const type = this.device && this.device.type
      const list = (type && DEVICE_METRICS[type]) ? DEVICE_METRICS[type] : []
      return [
        { value: '', text: 'Select a metric...' },
        ...list.map(m => ({ value: m.value, text: m.text }))
      ]
    },
    isValid () {
      return !!(
        this.form.metricName &&
        this.form.value !== null && this.form.value !== '' &&
        this.form.timestampLocal
      )
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler (v) {
        if (v) this.hydrate()
      }
    },
    'form.metricName' (newMetric) {
      // Auto-fill unit from metric label when adding a new reading and unit is blank
      if (this.mode === 'create' && !this.form.unit && newMetric && this.device) {
        const list = (this.device.type && DEVICE_METRICS[this.device.type]) || []
        const found = list.find(m => m.value === newMetric)
        if (found) {
          const match = found.text.match(/\(([^)]+)\)/)
          if (match) this.form.unit = match[1]
        }
      }
    }
  },
  methods: {
    hydrate () {
      if (this.reading && this.reading.id) {
        this.form = {
          metricName: this.reading.metricName || '',
          value: this.reading.value != null ? this.reading.value : null,
          unit: this.reading.unit || '',
          timestampLocal: this.reading.timestamp
            ? moment(this.reading.timestamp).format('YYYY-MM-DDTHH:mm')
            : moment().format('YYYY-MM-DDTHH:mm'),
          notes: this.reading.notes || '',
          isManual: this.reading.isManual != null ? this.reading.isManual : true,
          isFieldVerified: !!this.reading.isFieldVerified
        }
      } else {
        this.form = DEFAULT_FORM()
        if (this.defaultMetric) this.form.metricName = this.defaultMetric
      }
    },
    onHidden () {
      this.saving = false
      this.$emit('close')
    },
    buildInput () {
      const input = {
        metricName: this.form.metricName,
        value: parseFloat(this.form.value),
        timestamp: new Date(this.form.timestampLocal).toISOString(),
        isManual: !!this.form.isManual,
        isFieldVerified: !!this.form.isFieldVerified
      }
      if (this.form.unit && this.form.unit.trim()) input.unit = this.form.unit.trim()
      if (this.form.notes && this.form.notes.trim()) input.notes = this.form.notes.trim()
      return input
    },
    async submit (bvModalEvt) {
      if (bvModalEvt) bvModalEvt.preventDefault()
      if (!this.isValid || this.saving) return
      this.saving = true
      try {
        let result
        if (this.mode === 'edit') {
          result = await this.$apollo.mutate({
            mutation: UPDATE_MANUAL_READING_MUTATION,
            variables: { id: this.reading.id, input: this.buildInput() }
          })
          const updated = result.data && result.data.updateManualReading
          this.$emit('saved', updated)
        } else {
          const input = this.buildInput()
          input.device = { connect: { id: this.device.id } }
          result = await this.$apollo.mutate({
            mutation: CREATE_MANUAL_READING_MUTATION,
            variables: { input }
          })
          const created = result.data && result.data.createManualReading
          this.$emit('created', created)
        }
        this.$swal.fire({
          icon: 'success',
          title: this.mode === 'edit' ? 'Reading updated.' : 'Reading added.'
        })
        this.$emit('close')
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Manual reading save failed:', err)
        this.$swal.fire({
          icon: 'error',
          title: this.mode === 'edit' ? 'Failed to update reading' : 'Failed to add reading',
          text: err.message || 'An unexpected error occurred.',
          toast: false,
          position: 'center',
          showConfirmButton: true,
          timer: 0
        })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
